import React, { useContext, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import { messages } from './messages';
import style from './style.module.less';
import { FilterEditorContext } from '../../../../../../../components/filter-editor/context';
import { FulfillmentProvider } from '../../../../../../../containers/orders-filter-bar-container/filter-options/options/fulfillment-provider';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useIsSellerInTrial } from '../../../../../../../hooks/use-is-seller-in-trial';
import { useBTEmailExperienceNew } from '../../../../../../../hooks/use-seller-setting';
import { ACTION_TYPE_SEND_AN_EMAIL, ACTION_TYPE_DELEGATE_TO_ASSIGNED_FULFILLMENT_PROVIDER } from '../../../../constants/action-types';
import { AutomationRulesContext } from '../../../../context';
import { actionTypeIdHasMatchingComponent } from '../helpers/action-value-input-from-action-type';

/**
 * @typedef {object} ActionSelectorProps
 * @property {number | undefined} selectedActionTypeId - The currently selected action type ID
 * @property {function(number): void} onActionTypeChange - Callback when the action type changes
 */

/**
 * @param {ActionSelectorProps} props - The component props
 * @returns {React.JSX.Element} The rendered component
 */
export const ActionSelector = ({ onActionTypeChange, selectedActionTypeId }) => {
  const { ruleActionTypes, selectedRule, ruleTypesHashMap, defaultNewAction } = useContext(AutomationRulesContext);
  const { filterSet } = useContext(FilterEditorContext);
  const selectedActionTypeIdString = selectedActionTypeId?.toString();
  const isInTrial = useIsSellerInTrial();
  const enableNewEmailExperience = useBTEmailExperienceNew();

  const appliedFilters = filterSet?.orderFilterSetId?.toString() === selectedRule?.filterId?.toString() ? filterSet.appliedFilters : null;
  const showFulfillmentProviderDelegateServiceActionType = appliedFilters?.collectionFilters?.some(
    (f) => f.column === FulfillmentProvider().filterValue,
  );
  const getAvailableActions = useCallback(() => {
    // Apply all filters in a single pass using reduce
    return (ruleActionTypes || emptyArray).reduce((filteredActions, type) => {
      const { actionTypeId } = type;

      // Check if action should be excluded based on fulfillment provider delegate service
      if (
        !showFulfillmentProviderDelegateServiceActionType &&
        actionTypeId === ACTION_TYPE_DELEGATE_TO_ASSIGNED_FULFILLMENT_PROVIDER.toString()
      ) {
        return filteredActions;
      }

      // Check if action should be excluded based on trial/email experience
      if ((isInTrial || enableNewEmailExperience) && actionTypeId === ACTION_TYPE_SEND_AN_EMAIL.toString()) {
        return filteredActions;
      }

      // Check hasAccess property (if hasAccess is false and not the selected action, exclude it)
      if (!type.hasAccess && actionTypeId !== selectedActionTypeIdString) {
        return filteredActions;
      }

      // Action passed all filters, include it
      return [...filteredActions, type];
    }, []);
  }, [showFulfillmentProviderDelegateServiceActionType, ruleActionTypes, isInTrial, enableNewEmailExperience, selectedActionTypeIdString]);

  const onActionChange = useCallback(
    (actionTypeId) => {
      onActionTypeChange(Number.parseInt(actionTypeId, 10));
    },
    [onActionTypeChange],
  );

  const actionTypes = useMemo(() => {
    return getAvailableActions()
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      })
      .reduce((acc, actionType) => {
        if (actionTypeIdHasMatchingComponent(actionType.actionTypeId)) {
          // map to searchable dropdown format
          acc.push({
            value: actionType.actionTypeId,
            label: actionType.name,
          });
        }
        return acc;
      }, []);
  }, [getAvailableActions]);
  const showNoAccessBanner =
    selectedActionTypeId === defaultNewAction.actionTypeId ? false : !(ruleTypesHashMap || emptyObject)[selectedActionTypeId];

  return (
    <div className={style['action-type-selector']}>
      <div className={style['action-type-dropdown-wrapper']}>
        <SearchableDropdownField
          inlineMessage={showNoAccessBanner ? <FormattedMessage {...messages.actionTypeInactive} /> : null}
          inlineMessageType={showNoAccessBanner ? INLINE_MESSAGE_TYPES.error : null}
          label={<FormattedMessage {...messages.actionType} />}
          onItemSelected={onActionChange}
          options={actionTypes}
          selectedItemValue={selectedActionTypeIdString}
        />
      </div>
    </div>
  );
};
